import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    MobileStepper,
} from '@mui/material';
import { SCUIButton } from '@/atomic/atoms';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const TOTAL_STEPS = 4;

const STEP_IMAGES = ['/guide-step1.png', '/guide-step2.png', '/guide-step3.png', '/guide-step4.png'];

interface RoleRequestGuideModalProps {
    open: boolean;
    onClose: () => void;
    onRequestRole: () => void;
}

export const RoleRequestGuideModal: React.FC<RoleRequestGuideModalProps> = ({ open, onClose, onRequestRole }) => {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);

    useEffect(() => {
        if (!open) {
            setCurrentStep(0);
        }
    }, [open]);

    const handleNext = () => {
        setCurrentStep((prev) => Math.min(prev + 1, TOTAL_STEPS - 1));
    };

    const handleBack = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    };

    const stepKeys = ['step1', 'step2', 'step3', 'step4'] as const;
    const currentKey = stepKeys[currentStep];
    const isLastStep = currentStep === TOTAL_STEPS - 1;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                        display: 'flex',
                        flexDirection: 'column',
                        mx: { xs: 2, sm: 4 },
                        my: { xs: 2, sm: 4 },
                        maxHeight: { xs: 'calc(100vh - 32px)', sm: 'calc(100vh - 64px)' },
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'linear-gradient(to right, #007bff, #0056b3)',
                    color: 'white',
                    px: { xs: 2.5, sm: 4 },
                    py: 2,
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <HelpOutlineIcon sx={{ fontSize: 26 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                        {t('home.roleRequest.guideButtonText')}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: 1, overflowY: 'auto', flexGrow: 1 }}>
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                        mb: 2.5,
                        lineHeight: 0,
                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    }}
                >
                    <Box
                        component="img"
                        src={STEP_IMAGES[currentStep]}
                        alt={`step-${currentStep + 1}`}
                        sx={{
                            width: '100%',
                            display: 'block',
                            objectFit: 'contain',
                            maxHeight: { xs: 260, sm: 380 },
                        }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box
                        sx={{
                            background: 'linear-gradient(to right, #007bff, #0056b3)',
                            color: 'white',
                            borderRadius: '50%',
                            minWidth: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            flexShrink: 0,
                            mt: '1px',
                        }}
                    >
                        {currentStep + 1}
                    </Box>
                    <Box>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 700,
                                color: 'text.primary',
                                fontSize: { xs: '1.1rem', sm: '1.2rem' },
                                lineHeight: 1.4,
                                mb: 0.5,
                            }}
                        >
                            {t(`home.roleRequest.guideModal.${currentKey}Title`)}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? theme.palette.grey[300]
                                        : theme.palette.grey[900],
                                lineHeight: 1.6,
                                fontSize: { xs: '1rem', sm: '1.0625rem' },
                            }}
                        >
                            {t(`home.roleRequest.guideModal.${currentKey}Desc`)}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <Box sx={{ px: { xs: 2.5, sm: 3 }, pb: 0.5, flexShrink: 0 }}>
                <MobileStepper
                    variant="dots"
                    steps={TOTAL_STEPS}
                    position="static"
                    activeStep={currentStep}
                    sx={{
                        background: 'transparent',
                        px: 0,
                        '& .MuiMobileStepper-dot': {
                            width: 10,
                            height: 10,
                        },
                        '& .MuiMobileStepper-dotActive': {
                            backgroundColor: '#007bff',
                        },
                    }}
                    nextButton={<Box />}
                    backButton={<Box />}
                />
            </Box>

            <DialogActions
                sx={{
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    px: { xs: 2.5, sm: 3 },
                    py: 2,
                    gap: 1.5,
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <SCUIButton
                    onClick={handleBack}
                    disabled={currentStep === 0}
                    type="secondary"
                    sizeType="default"
                    size="m"
                    text={t('home.roleRequest.guideModal.prev')}
                    showStartIcon
                    startIcon={<ArrowBackIcon />}
                />

                {isLastStep ? (
                    <SCUIButton
                        onClick={onRequestRole}
                        type="primary"
                        sizeType="default"
                        size="m"
                        text={t('home.roleRequest.buttonText')}
                        showStartIcon
                        startIcon={<SecurityIcon />}
                    />
                ) : (
                    <SCUIButton
                        onClick={handleNext}
                        type="primary"
                        sizeType="default"
                        size="m"
                        text={t('home.roleRequest.guideModal.next')}
                        showEndIcon
                        endIcon={<ArrowForwardIcon />}
                    />
                )}
            </DialogActions>
        </Dialog>
    );
};
